import React, { useRef, useState, useEffect, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faNewspaper, faExternalLinkAlt, faSpinner } from "@fortawesome/free-solid-svg-icons";

const COMMODITY_URL = "https://krishijagran.com/commodity-news";

// After onLoad throws SecurityError (Chrome can't distinguish blocked vs cross-origin success),
// wait this long before assuming the iframe is blocked and opening a new tab.
const CHROME_FALLBACK_MS = 1500;

const CommodityNews = () => {
  const iframeRef = useRef(null);
  const resolvedRef = useRef(false);
  const fallbackTimerRef = useRef(null);
  const [status, setStatus] = useState("loading"); // "loading" | "ok" | "blocked"

  const markBlocked = useCallback(() => {
    if (resolvedRef.current) return;
    resolvedRef.current = true;
    clearTimeout(fallbackTimerRef.current);
    setStatus("blocked");
    window.open(COMMODITY_URL, "_blank", "noopener,noreferrer");
  }, []);

  const markOk = useCallback(() => {
    if (resolvedRef.current) return;
    resolvedRef.current = true;
    clearTimeout(fallbackTimerRef.current);
    setStatus("ok");
  }, []);

  const handleLoad = useCallback(() => {
    try {
      // Firefox blocked case: browser navigates iframe to about:blank (same-origin accessible).
      // contentDocument is readable but body is empty.
      const doc = iframeRef.current?.contentDocument;
      if (doc && doc.body && doc.body.innerHTML.trim() !== "") {
        markOk();
      } else {
        markBlocked();
      }
    } catch (e) {
      // Chrome: throws SecurityError for BOTH:
      //   - iframe blocked by X-Frame-Options (chrome-error:// page, cross-origin)
      //   - iframe loaded successfully with cross-origin content
      // We cannot distinguish these in Chrome, so wait CHROME_FALLBACK_MS then assume blocked.
      fallbackTimerRef.current = setTimeout(markBlocked, CHROME_FALLBACK_MS);
    }
  }, [markOk, markBlocked]);

  // Safety net if onLoad never fires (network failure etc.)
  useEffect(() => {
    const safety = setTimeout(markBlocked, 15000);
    return () => {
      clearTimeout(safety);
      clearTimeout(fallbackTimerRef.current);
    };
  }, [markBlocked]);

  return (
    <div style={s.page}>
      {/* Header */}
      <div style={s.header}>
        <div style={s.headerLeft}>
          <FontAwesomeIcon icon={faNewspaper} style={s.headerIcon} />
          <span style={s.headerTitle}>Commodity News</span>
        </div>
        <a
          href={COMMODITY_URL}
          target="_blank"
          rel="noopener noreferrer"
          style={s.newTabBtn}
        >
          <FontAwesomeIcon icon={faExternalLinkAlt} style={{ marginRight: 6 }} />
          Open in New Tab
        </a>
      </div>

      {/* Loading overlay */}
      {status === "loading" && (
        <div style={s.overlay}>
          <FontAwesomeIcon icon={faSpinner} spin style={s.spinner} />
          <div style={s.overlayText}>Checking embedding support…</div>
        </div>
      )}

      {/* Blocked fallback */}
      {status === "blocked" && (
        <div style={s.overlay}>
          <FontAwesomeIcon icon={faNewspaper} style={{ fontSize: 44, color: "#29553D", marginBottom: 16 }} />
          <div style={s.overlayTitle}>Opened in a new tab</div>
          <div style={s.overlayText}>
            Krishi Jagran does not allow embedding inside the CRM.
            The page has been automatically opened in a new tab.
          </div>
          <a
            href={COMMODITY_URL}
            target="_blank"
            rel="noopener noreferrer"
            style={s.fallbackBtn}
          >
            <FontAwesomeIcon icon={faExternalLinkAlt} style={{ marginRight: 6 }} />
            Open Again
          </a>
        </div>
      )}

      {/* Iframe — always mounted so onLoad fires; visible only when ok */}
      <iframe
        ref={iframeRef}
        src={COMMODITY_URL}
        title="Commodity News"
        width="100%"
        height="900"
        style={{ border: "none", display: status === "ok" ? "block" : "none" }}
        onLoad={handleLoad}
      />
    </div>
  );
};

const s = {
  page: {
    padding: "24px 24px 40px",
    maxWidth: 1200,
    margin: "0 auto",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 20,
    padding: "14px 20px",
    backgroundColor: "#29553D",
    borderRadius: 10,
    color: "#fff",
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  headerIcon: {
    fontSize: "1.3rem",
    color: "#a8d5b5",
  },
  headerTitle: {
    fontSize: "1.15rem",
    fontWeight: 700,
    letterSpacing: "0.01em",
  },
  newTabBtn: {
    display: "inline-flex",
    alignItems: "center",
    padding: "7px 16px",
    backgroundColor: "rgba(255,255,255,0.15)",
    border: "1px solid rgba(255,255,255,0.3)",
    borderRadius: 6,
    color: "#fff",
    fontSize: "0.85rem",
    fontWeight: 500,
    textDecoration: "none",
  },
  overlay: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 420,
    gap: 12,
  },
  spinner: {
    fontSize: 36,
    color: "#29553D",
  },
  overlayTitle: {
    fontSize: "1.1rem",
    fontWeight: 700,
    color: "#1a3328",
  },
  overlayText: {
    fontSize: "0.9rem",
    color: "#666",
    textAlign: "center",
    maxWidth: 420,
    lineHeight: 1.6,
  },
  fallbackBtn: {
    marginTop: 8,
    display: "inline-flex",
    alignItems: "center",
    padding: "10px 22px",
    backgroundColor: "#29553D",
    borderRadius: 7,
    color: "#fff",
    fontSize: "0.9rem",
    fontWeight: 600,
    textDecoration: "none",
  },
};

export default CommodityNews;
